import { FormEvent, useEffect, useRef, useState } from "react";
import { GiSettingsKnobs } from "react-icons/gi";
import { Modal } from "@/common/components/modal";
import { Pagination } from "@/common/components/pagination";
import { TableModals } from "@/common/components/Table/subs/modals";
import { ALL_PRODUCTS_URL, ORDERS_URL } from "@/common/constants";
import { KeyValueObj } from "@/common/types";
import { formatToNaira, toParamsFormat } from "@/common/utils/general";
import { formatQuantity, readBatchUnit } from "@/common/utils/quantity";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import useQuery from "@/hooks/useQuery";
import { useStore } from "@/hooks/useContexts";
import { Search } from "../../../products/subs/styles";
import { CatalogueFilterOptions, CatalogueSortOptions } from "./data";
import { buildOrderLine } from "../buildOrderLine";
import {
  ProductOrderContent,
  ISelectedProduct,
  ISelectedBatch,
} from "./product-order-content";
import OrderByProductSearch, { OrderByProductSearchSkeleton } from ".";

export default function AllProductsOrder() {
  const apiCall = useApiCallHandling();
  const { setCurrentLevel2Modal, setLevel2ModalOptions } = useStore();

  const [productPage, setProductPage] = useState(1);
  const [productPerPage, setProductPerPage] = useState(25);
  const [productPageCount, setProductPageCount] = useState(1);
  const [catalogueFilters, setCatalogueFilters] = useState<KeyValueObj>({});
  const hasActiveCatalogueFilters = Object.values(catalogueFilters).some(
    (value) => value !== undefined && value !== null && value !== "",
  );
  const [searchTerm, setSearchTerm] = useState<any | string>("");
  const [catalogueSort, setCatalogueSort] = useState<{
    sortBy: string;
    sortDirection: string;
  }>({ sortBy: "", sortDirection: "" });
  const catalogueSearchTimer = useRef<ReturnType<typeof setTimeout>>();

  const catalogueQueryParams = toParamsFormat({
    page: productPage,
    per_page: productPerPage,
    search_term: searchTerm,
    sort_by: catalogueSort.sortBy,
    sort_direction: catalogueSort.sortDirection,
    ...catalogueFilters,
  });

  const {
    data: inStockProducts,
    loading: inStockLoading,
    meta: inStockMeta,
  } = useQuery(`${ALL_PRODUCTS_URL}${catalogueQueryParams}`);

  useEffect(() => {
    typeof inStockMeta.total_pages === "number" &&
      setProductPageCount(inStockMeta.total_pages);
  }, [inStockMeta]);

  function handleProductNextPage(event: { selected: number }) {
    // MED-708: Pagination already hands back a 1-based page (see its own
    // onPageChange), so adding one again skipped page 1 and asked for one
    // page past the end — the catalogue tab paged, it just paged wrong.
    setProductPage(event.selected);
  }

  function handleProductPerPage(event: FormEvent<HTMLInputElement>) {
    const { value } = event.currentTarget;
    value.length > 0 && setProductPerPage(+value);
  }

  // MED-699: filters combine with free-text search rather than replacing it —
  // both feed the same request. Debounced the same way Table's own search is.
  function handleCatalogueSearch(value: string) {
    clearTimeout(catalogueSearchTimer.current);
    catalogueSearchTimer.current = setTimeout(() => {
      setProductPage(1);
      setSearchTerm(value);
    }, 500);
  }

  function handleCatalogueFilter(values: KeyValueObj) {
    setCurrentLevel2Modal("");
    setProductPage(1);
    setCatalogueFilters(values);
  }

  function openCatalogueFilter() {
    setLevel2ModalOptions({
      filterInputs: CatalogueFilterOptions,
      onFilter: handleCatalogueFilter,
      // So reopening the modal shows what's actually applied instead of
      // resetting to blank — see Filter's initialValues in Table/filter.
      filterValues: catalogueFilters,
    });
    setCurrentLevel2Modal("table-filters");
  }

  function handleCatalogueSortChange(event: FormEvent<HTMLSelectElement>) {
    const option = CatalogueSortOptions[+event.currentTarget.value];
    setProductPage(1);
    setCatalogueSort({
      sortBy: option.sortBy,
      sortDirection: option.sortDirection,
    });
  }

  const [selectedProduct, setSelectedProduct] =
    useState<ISelectedProduct | null>(null);
  const [showProductPreview, setShowProductPreview] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<KeyValueObj>({});
  const [selectedRawItem, setSelectedRawItem] = useState<any>(null);
  const [selectedBatches, setSelectedBatches] = useState<ISelectedBatch[]>([]);
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState("");
  const [orderNote, setOrderNote] = useState("");
  const [submittingOrder, setSubmittingOrder] = useState(false);

  const products = ((inStockProducts as any[]) || []).map((item: any) => {
    const batches: any[] = item?.batches ?? [];
    const totalQty = batches.reduce(
      (sum: number, b: any) => sum + (b?.quantity_in_stock ?? 0),
      0,
    );
    // MED-697: the server sends the product-level total already read in
    // this product's packaging levels, over exactly the batches this buyer
    // is allowed to see. The base-unit sum stays as the fallback for a
    // product with no packaging hierarchy - it is no longer labelled "Cartons".
    const availableLabel = formatQuantity(item?.quantity_display, totalQty);

    // MED-706: the card shows one price and one unit label. The catalogue
    // merges every priced seller's batches onto one product
    // (DrugSerializer#build_batches is unscoped by owner), so `batches` can
    // carry several prices. The backend's price filter/sort compare against
    // the CHEAPEST (Product::CHEAPEST_PRICE_SQL); the card must show that same
    // one, or a price-range filter returns a card whose visible price sits
    // outside the band. Read the label/multiplier from THAT batch's own
    // selling level (MED-697), not the product's outermost packaging layer.
    // Falls back to the first batch, then a bare unit.
    const pricedBatches = batches.filter(
      (b: any) => parseFloat(b?.selling_price_per_unit) > 0,
    );
    const priceBatch = pricedBatches.length
      ? pricedBatches.reduce((cheapest: any, b: any) =>
          parseFloat(b.selling_price_per_unit) <
          parseFloat(cheapest.selling_price_per_unit)
            ? b
            : cheapest,
        )
      : (batches[0] ?? {});
    const priceUnit = readBatchUnit(priceBatch?.quantity_display);
    const unitPriceRaw = parseFloat(priceBatch?.selling_price_per_unit ?? "0");

    return {
      id: item?.id ?? "",
      name: item?.brand_name ?? "",
      company: item?.owner?.name ?? "",
      batchNo:
        batches.length > 1
          ? `${batches.length} batches`
          : (batches[0]?.batch_number ?? ""),
      expiryDate: "",
      // MED-697: the server's reading, summed PER BATCH and at the level this
      // holder sells at. `Math.floor(totalQty / baseUnitCount)` was the previous
      // line here and reports packages nobody can ship: six part-batches summing
      // to 47 tablets divide into four cartons of ten, when only three whole
      // cartons exist and the fourth would be built from loose tablets across
      // six expiry dates. It also read at the product's outermost level, so a
      // distributor who sells by the pack was shown cartons.
      available: availableLabel,
      imageUrl: item?.images?.[0]?.url ?? "",
      status: "available" as const,
      price: formatToNaira(unitPriceRaw * priceUnit.multiplier),
      packagingType: priceUnit.label,
    };
  });

  function closeProductPreview() {
    setShowProductPreview(false);
    setSelectedProduct(null);
    setSelectedRawItem(null);
    setSelectedBatches([]);
    setExpectedDeliveryDate("");
    setOrderNote("");
  }

  function handleProductOrder() {
    if (!selectedProduct || !selectedRawItem || !selectedAddress?.id) return;
    setSubmittingOrder(true);

    // Same line builder as the supplier wizard (make-order/index.tsx) so an
    // order for the same product/batch/quantity produces an identical
    // OrderProduct row whichever tab it came from. Each selected batch carries
    // its own selling-level multiplier from the batch picker; buildOrderLine
    // converts to base units per batch.
    const payload = {
      shipping_address_id: selectedAddress.id,
      supplier_id: selectedRawItem.owner_id,
      expected_delivery_date: expectedDeliveryDate,
      buyer_note: orderNote,
      products: [
        buildOrderLine({
          product: selectedRawItem,
          batches: selectedBatches.map((batch) => ({
            inventory_id: batch.id,
            amount: batch.amount,
            multiplier: batch.multiplier,
          })),
        }),
      ],
    };

    apiCall(
      ORDERS_URL,
      "POST",
      payload,
      () => closeProductPreview(),
      (err: any) => console.log(err),
      () => setSubmittingOrder(false),
    );
  }

  return (
    <div className="w-full">
      <Modal isOpen={showProductPreview} close={closeProductPreview}>
        <ProductOrderContent
          key={selectedProduct?.id}
          product={selectedProduct}
          batches={selectedRawItem?.batches ?? []}
          selectedBatches={selectedBatches}
          setSelectedBatches={setSelectedBatches}
          selectedAddress={selectedAddress}
          setSelectedAddress={setSelectedAddress}
          expectedDeliveryDate={expectedDeliveryDate}
          setExpectedDeliveryDate={setExpectedDeliveryDate}
          orderNote={orderNote}
          setOrderNote={setOrderNote}
          submittingOrder={submittingOrder}
          onSubmit={handleProductOrder}
        />
      </Modal>

      <TableModals />

      <div className="flex flex-wrap items-center justify-start gap-6 mt-2">
        <Search className="flex-1 min-w-[240px]">
          <div className="search-wrapper w-full">
            <input
              className="search-input"
              type="text"
              placeholder="Search products"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                handleCatalogueSearch(e.target.value);
              }}
            />
          </div>
        </Search>
        <div className="flex flex-wrap items-center justify-start gap-4">
          <button
            type="button"
            onClick={openCatalogueFilter}
            className="relative flex w-40 shrink-0 cursor-pointer items-center gap-[1.1rem] rounded-lg border border-[#EAEAEA] py-2 pl-4 pr-8 text-sm font-normal text-[#646F7B] hover:shadow-[1px_1px_7px_#336CFBAA]"
          >
            <GiSettingsKnobs className="rotate-90 text-[1.3rem] text-[#33145f]" />
            Filter by
            {hasActiveCatalogueFilters && (
              <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-[#33145f]" />
            )}
          </button>
          <select
            value={CatalogueSortOptions.findIndex(
              (option) =>
                option.sortBy === catalogueSort.sortBy &&
                option.sortDirection === catalogueSort.sortDirection,
            )}
            onChange={handleCatalogueSortChange}
            className="w-40 shrink-0 cursor-pointer rounded-lg border border-[#EAEAEA] py-2 pl-4 pr-8 text-sm font-normal text-[#646F7B] hover:shadow-[1px_1px_7px_#336CFBAA]"
          >
            {CatalogueSortOptions.map((option, i) => (
              <option key={option.label} value={i}>
                Sort: {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {inStockLoading ? (
        // Filter/sort/search/clear all funnel into the same
        // catalogueQueryParams -> useQuery refetch, so this one loading flag
        // already covers every one of those cases — no separate
        // "filtering"/"clearing" state needed.
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <OrderByProductSearchSkeleton key={i} />
          ))}
        </div>
      ) : (
        <>
          {products.length === 0 && (
            <p className="py-6 text-center text-sm text-neutral-500">
              No products in stock at the moment.
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {products.map((product) => (
              <OrderByProductSearch
                key={product.id}
                name={product.name}
                company={product.company}
                batchNo={product.batchNo}
                expiryDate={product.expiryDate}
                available={product.available}
                imageUrl={product.imageUrl}
                status={product.status}
                price={product.price}
                packagingType={product.packagingType}
                onOrder={() => {
                  const rawItem = (inStockProducts as any[]).find(
                    (i: any) => i.id === product.id,
                  );
                  setSelectedRawItem(rawItem ?? null);
                  setSelectedProduct(product);
                  setShowProductPreview(true);
                }}
              />
            ))}
          </div>
        </>
      )}
      <Pagination
        onNextPage={handleProductNextPage}
        onPerPageChange={handleProductPerPage}
        pageCount={productPageCount}
      />
    </div>
  );
}
